// A/B "v2.5" arm (DEV-4667): the redesigned offers card. Rendered on the v2.5 `-b` base
// (/dopunsko-zdravstveno-osiguranje-b/ponude, /dopunsko-zdravstveno-osiguranje-b/ponude-pregled).
// The canonical OfferCard (offer.card.tsx) is the pre-redesign original served on the baseline
// base (/dopunsko-zdravstveno-osiguranje/ponude, .../ponude-pregled).
import { useMutation } from "@apollo/client";
import styled from "@emotion/styled";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatNumber, RootState, setEditableCoverageSelection, setGlobalLoading, setOfferId, setOneTimeToggle, useAppDispatch, useAppSelector } from "src/common";
import { AdditionalCoveragesCard, Button, CheckIcon, Flex, Loader, OfferPriceSummary, OfferSupplementaryToggleRow, Text } from 'src/components';
import { AppRoutes } from "src/pages/app";
import { colors } from "src/theme";
import { CalculationResponseDto, CalculatorCoverageWithPriceDto, CalculatorFixedCoverageDto, createCheckoutHealthOffer } from "../../common/api/apollo/supplementary";
// Direct import (not the barrel): styled(ActionLabelCard) runs at module init, and the
// components barrel re-exports this file too — going through it would be a cycle and
// leave ActionLabelCard undefined here.
import { ActionLabelCard } from "./actionLabel.card";
import { Card } from "./card";

const CardStyle = styled(Card)({
  padding: '16px 8px',

  '@media (min-width: 576px)': {
    padding: '8px',
  },
})

// `&&` doubles the class selector so this wins over Card's own radius rule
// regardless of emotion's style-insertion order.
const ActionLabelCardV2 = styled(ActionLabelCard)({
  '&&': {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: colors.note3
  },
})

const OfferCardRoot = styled(CardStyle)({
  position: 'relative',
})

const HeaderLogo = styled.img({
  maxHeight: '44px',
  minHeight: '24px',
  maxWidth: '120px',
  flexShrink: 0,
})

// When an action label is present it sits flush on top, so the box squares its
// top corners to merge with it; otherwise the box is fully rounded.
const CoveragesBox = styled.div<{ flushTop?: boolean }>((p) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  background: colors.background6,
  padding: 4,
  paddingBottom: 8,
  borderTopLeftRadius: p.flushTop ? 0 : 12,
  borderTopRightRadius: p.flushTop ? 0 : 12,
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
  transition: 'all 1s ease-out'
}))

const DetailsButton = styled(Button)({
  position: "absolute",
  right: 4
})

const CoverageDescription = styled.div<{ open?: boolean }>((p) => ({
  display: "grid",
  gridTemplateRows: p.open ? "1fr" : "0fr",
  transition: "grid-template-rows 0.25s ease",
  "& > div": {
    overflow: "hidden",
    minHeight: 0
  }
}))

const AdditionalTriggerBox = styled.button({
  width: '100%',
  border: 'none',
  cursor: 'pointer',
  background: colors.background6,
  borderRadius: 10,
  padding: '12px 16px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
})

const CalculationsLoadingOverlay = styled.div({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255, 255, 255, 0.55)',
  borderRadius: 'inherit',
  zIndex: 3,
})

// Coverage order in the summary list (Figma).
const COVERAGE_SORT_ORDER = [
  "B lista lijekova",
  "Iskaznica",
  "OTHERS",
  "Svi troškovi participacije su pokriveni neograničeno",
];

const sortCoverages = (coverages: CalculatorFixedCoverageDto[]) =>
  coverages.slice().sort((a, b) => {
    const ia = COVERAGE_SORT_ORDER.indexOf(a.label);
    const ib = COVERAGE_SORT_ORDER.indexOf(b.label);
    const otherIdx = COVERAGE_SORT_ORDER.indexOf("OTHERS");
    return (ia === -1 ? otherIdx : ia) - (ib === -1 ? otherIdx : ib);
  });

interface OfferV25CardProps {
  offer: CalculationResponseDto;
  loading?: boolean;
  oneTimeToggle?: boolean;
  bListToggle?: boolean;
  className?: string;
  showSelectButton?: boolean;
  /** True while supplementary pricing query is refetching (e.g. after toggling editOnWeb coverage). */
  calculationsLoading?: boolean;
  onSupplementaryCoverageToggle?: () => void;
  isComparisonFunnel?: boolean;
}

export const OfferV25Card = ({ offer, loading, oneTimeToggle = false, className, showSelectButton = true, calculationsLoading = false, isComparisonFunnel = false }: OfferV25CardProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [createCheckoutHealthOfferMutation] = useMutation(createCheckoutHealthOffer);
  const soIdWeb = useAppSelector((store: RootState) => store.model.offer.soIdWeb);
  const editableCoverageSelections = useAppSelector((store: RootState) => store.model.offer.editableCoverageSelections);

  const [isMoreDetailsOpen, setIsMoreDetailsOpen] = useState(false);
  const [isAdditionalOpen, setIsAdditionalOpen] = useState(false);
  // Kept for checkout: which payment mode was chosen (driven by the page-level filter).
  const [isYearToggleChecked, setIsYearToggleChecked] = useState(oneTimeToggle);

  useEffect(() => {
    setIsYearToggleChecked(oneTimeToggle);
  }, [oneTimeToggle]);

  // editOnWeb coverages (e.g. eCard / "Zeleni popust") render as toggles below,
  // so we exclude them from "Moguća dodatna pokrića" to avoid duplication.
  const nonEditableAdditionalCoverages = (offer?.coverages ?? []).filter(
    (c) => c?.editOnWeb !== true
  );

  const editableCoverages = (offer?.coverages ?? []).filter(
    (c) => c.editOnWeb && (!c.type || c.type === 'checkbox')
  );

  useEffect(() => {
    if (!offer?.code || editableCoverages.length === 0) return;
    editableCoverages.forEach((cov: { code: string }) => {
      dispatch(setEditableCoverageSelection({ code: cov.code, value: editableCoverageSelections?.[cov.code] ?? false }));
    });
  }, [offer?.code, editableCoverages.length]);

  if (loading) {
    return (
      <Card radius={12} padding={20} className={className}>
        <Loader />
      </Card>
    )
  }

  const monthlyPrice = offer.prices.generalTransferWithInstallments / 12;
  const yearlyPrice = offer.prices.generalTransfer / 12;
  const sortedCoverages = sortCoverages(offer.fixedCoverages || []);

  const renderComparisonPrice = () => {
    if (!isComparisonFunnel || typeof offer.diffPrice !== 'number') return null;
    const positive = offer.diffPrice >= 0;
    return (
      <Flex gap={4} justify='space-between' align='center' style={{ background: positive ? '#ffc' : '#EBFBFF', borderRadius: 8, padding: '4px 8px' }}>
        <Text type='small' color={colors.text2}>{positive ? 'Ti štediš' : 'Ova polica je skuplja za'}</Text>
        <Text type='base' bold color={colors.text2}>{`${positive ? '+' : ''}${formatNumber(Math.abs(offer.diffPrice))}`}</Text>
      </Flex>
    );
  };

  const selectOffer = () => {
    const selectedFromCard = editableCoverages
      .filter((c: { code: string }) => editableCoverageSelections?.[c.code] === true)
      .map((c: { code: string }) => c.code);
    const withPageLevel = editableCoverageSelections?.eCard ? [...selectedFromCard, 'eCard'] : selectedFromCard;
    const selectedCodes = Array.from(new Set(withPageLevel));
    dispatch(setGlobalLoading(true));
    createCheckoutHealthOfferMutation({
      variables: {
        id: soIdWeb,
        input: {
          productCode: offer.code,
          coverages: selectedCodes
        }
      }
    })
      .then((response) => {
        const offerId = response.data.createHealthOffer;
        dispatch(setOfferId(offerId));
        dispatch(setOneTimeToggle(isYearToggleChecked));
        navigate(`${AppRoutes.OFFER.fullPath}/${offerId}`);
      })
      .catch((error) => {
        console.error('Greška prilikom izvršavanja mutacije:', error);
      })
      .finally(() => {
        dispatch(setGlobalLoading(false));
      });
  };

  return (
    <OfferCardRoot radius={12} className={className}>
      <Flex vertical gap={12}>
        {/* Header: logo + brand left, monthly price right */}
        <Flex justify="space-between">
          <HeaderLogo src={offer.logoUrl ?? ''} alt={offer.brandName ?? 'logo'} />
          { /* <Text type="medium" bold color={colors.text1}>{offer.brandName}</Text> */ }
          <Flex align="baseline" justify="flex-end" gap={4}>
            <Text type="xxLarge" bold color={colors.text1}>{`${formatNumber(isYearToggleChecked ? yearlyPrice : monthlyPrice)}€`}</Text>
            <Text type="small" color={colors.text3}>/12mj</Text>
          </Flex>
        </Flex>

        {renderComparisonPrice()}

        {/* Action label sits flush on top of the coverages box (no gap). */}
        <Flex vertical gap={0}>
          <ActionLabelCardV2 text={offer.actionLabel || ''} />

          {/* Coverages summary with "Detalji" toggle */}
          <CoveragesBox flushTop={!!offer.actionLabel}>
            <Flex vertical gap={6}>
              {sortedCoverages.map((coverage, index) => (
                <Flex key={coverage.code ?? index} vertical gap={2}>
                  <Flex align="flex-start" gap={12}>
                    <CheckIcon color={colors.success} />
                    <Flex vertical style={{ width: '100%' }}>
                      <Text type="small" color={colors.text2}>{coverage.label}</Text>
                      {coverage.description && (
                        <CoverageDescription open={isMoreDetailsOpen}>
                          <div>
                            <Text type="tiny" color={colors.text3}>{parse(coverage.description)}</Text>
                          </div>
                        </CoverageDescription>
                      )}
                    </Flex>
                  </Flex>
                </Flex>
              ))}
            </Flex>
            <DetailsButton
              btnType="textPrimary"
              style={{ padding: 0 }}
              onClick={() => setIsMoreDetailsOpen((v) => !v)}
              text={<Text type="small" color={colors.primary} bold>{ isMoreDetailsOpen ? "Prikaži manje" : "Detalji" }</Text>}
            />
          </CoveragesBox>
        </Flex>

        <OfferPriceSummary
          installmentsTotal={offer.prices.generalTransferWithInstallments}
          oneTimeTotal={offer.prices.generalTransfer}
          priceWithoutDiscounts={offer.priceWithoutDiscounts}
        />

        {showSelectButton && (
          <Button
            btnType="primary"
            onClick={() => selectOffer()}
            text="Odaberi"
            style={{ width: '100%' }}
          />
        )}

        {nonEditableAdditionalCoverages.length > 0 && (
          <>
            <AdditionalTriggerBox type="button" onClick={() => setIsAdditionalOpen((v) => !v)}>
              <Text type="base" bold color={colors.primary}>+ Moguća dodatna pokrića +</Text>
              <Text type="tiny" color={colors.text3}>*Nije moguće ugovoriti online</Text>
            </AdditionalTriggerBox>
            {isAdditionalOpen && (
              <AdditionalCoveragesCard additionalCoverages={nonEditableAdditionalCoverages as CalculatorCoverageWithPriceDto[]} />
            )}
          </>
        )}

        {editableCoverages.map((cov: CalculatorCoverageWithPriceDto) => (
          <OfferSupplementaryToggleRow
            key={cov.code}
            title={cov.label ?? cov.code}
            description={cov.description || ''}
            checked={editableCoverageSelections?.[cov.code] === true}
            onToggleChange={(checked) =>
              dispatch(setEditableCoverageSelection({ code: cov.code, value: checked }))
            }
          />
        ))}
      </Flex>

      {calculationsLoading && (
        <CalculationsLoadingOverlay aria-busy="true" aria-live="polite">
          <Loader size={32} />
        </CalculationsLoadingOverlay>
      )}
    </OfferCardRoot>
  )
};
